import { useEffect, useState } from 'react';
import {
  DevHubBox,
  DevHubButton,
  DevHubCircularProgress,
  DevHubGrid,
} from '@devxdiscover/devhub-ui';
import { Device } from './insights/types/InsightsTypes';
import DeviceSearchGlobal from './insights/devices/DeviceSearchGlobal';
import DeviceCard from './insights/devices/DeviceCard';
import { CSVLink } from 'react-csv';
import { gql, useLazyQuery } from '@apollo/client';
import { Content } from '@backstage/core-components';
import { asExportableDevices } from '../util/export';

/**
 * DeviceListPage Component - Displays a list of devices
 * @returns JSX.Element
 */
const DeviceListPage = () => {
  const SearchDevicesQuery = gql`
    query GlobalDeviceList(
      $filter: ResourceCatalogDeviceFilter
      $pagination: PaginationInput
    ) {
      resources {
        devicesPaged(filter: $filter, pagination: $pagination) {
          devices {
            deviceId
            hostname
            type
            fqdn
            ipAddress
            environment
            location
            serialNumber
            tier
            href
            applicationsPaged {
              applications {
                name
              }
            }
            osInfo {
              make
              name
              release
              edition
              version
            }
          }
          totalCount
        }
      }
    }
  `;

  const [devices, setDevices] = useState<Device[]>([]);
  const [hasMoreResults, setHasMoreResults] = useState<boolean>(true);
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [selectedEnvs, setSelectedEnvs] = useState([] as string[]);
  const [selectedTiers, setSelectedTiers] = useState([] as string[]);
  const [loadingState, setLoadingState] = useState(false);
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [offset, setOffset] = useState(0);
  const [searchString, setSearchString] = useState<string[]>([]);

  const filterFunction = (filterTerm: string[]) => {
    return {
      anyOf: {
        fqdns: filterTerm,
        ipAddresses: filterTerm,
        serialNumbers: filterTerm,
      },
      ...(selectedEnvs.length > 0 && { environments: selectedEnvs }),
      ...(selectedTiers.length > 0 && { tiers: selectedTiers }),
    };
  };

  const [getDevices, { loading, error, fetchMore }] = useLazyQuery(
    SearchDevicesQuery,
    {
      errorPolicy: 'all',
      nextFetchPolicy: 'cache-first',
      variables: {
        pagination: {
          limit: 100,
          offset: offset,
        },
      },
    },
  );

  useEffect(() => {
    setOffset(0);
    const variables = {
      filter: filterFunction(searchString),
      pagination: {
        limit: 100,
        offset: 0,
      },
    };
    if (searchExecuted) {
      getDevices({ variables }).then(res => {
        if (res?.data?.resources?.devicesPaged?.devices) {
          setDevices(res?.data.resources.devicesPaged.devices);
        }
      });
    }
    setHasMoreResults(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvs, selectedTiers]);

  const handleEnvChange = (env: string) => {
    const isSelected = selectedEnvs?.includes(env);
    const updatedEnvs = isSelected
      ? selectedEnvs.filter(e => e !== env)
      : [...selectedEnvs, env];

    setSelectedEnvs(updatedEnvs);
  };

  const handleTierChange = (tier: string) => {
    const isSelected = selectedTiers?.includes(tier);

    const updatedTiers = isSelected
      ? selectedTiers?.filter(t => t !== tier)
      : [...selectedTiers, tier];

    setSelectedTiers(updatedTiers);
  };

  const handleSearch = (searchTerm: string[]) => {
    if (searchTerm.length === 0) return;

    const trimmedTerms = searchTerm.map(term => term.trim());
    setSearchString(trimmedTerms);
    setOffset(0);

    const variables = {
      filter: filterFunction(trimmedTerms),
      pagination: {
        limit: 100,
        offset: 0,
      },
    };
    setSearchExecuted(true);
    setHasMoreResults(true);
    try {
      getDevices({ variables }).then(res => {
        if (res?.data?.resources?.devicesPaged?.devices) {
          setDevices(res?.data.resources.devicesPaged.devices);
        }
      });
    } catch (err) {
      setErrorOccurred(true);
    }
  };

  const handleLoadMore = async () => {
    setLoadingState(true);

    try {
      const res = await fetchMore({
        variables: {
          filter: filterFunction(searchString),
          pagination: {
            limit: 100,
            offset: offset + 100,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return fetchMoreResult;
        },
      });

      setDevices((prev: Device[]) => [
        ...prev,
        ...res?.data?.resources?.devicesPaged?.devices,
      ]);

      const total = res?.data?.resources?.devicesPaged?.totalCount;
      if (devices?.length === total) {
        setHasMoreResults(false);
      }

      setLoadingState(false);
    } catch (err) {
      setErrorOccurred(true);
    }
    setOffset(offset + 100);
  };

  const clearDeviceList = () => {
    setDevices([]);
    setOffset(0);
    setSearchString([]);
  };

  return (
    <Content>
      <DeviceSearchGlobal
        loading={loading}
        error={error}
        handleEnvChange={handleEnvChange}
        selectedEnvs={selectedEnvs}
        handleTierChange={handleTierChange}
        selectedTiers={selectedTiers}
        deviceList={devices}
        multiline
        searchExecuted={searchExecuted}
        setSearchExecuted={setSearchExecuted}
        handleSearch={handleSearch}
        clearDeviceList={clearDeviceList}
        hasMoreResults={hasMoreResults}
        errorOccurred={errorOccurred}
      />
      {devices?.length > 0 && (
        <DevHubBox display="flex" justifyContent="flex-end" mb={2}>
          <CSVLink filename="DeviceList" data={asExportableDevices(devices)}>
            <DevHubButton variant="contained" color="primary">
              Export
            </DevHubButton>
          </CSVLink>
        </DevHubBox>
      )}

      <DevHubBox>
        <DevHubGrid container>
          {devices.map((device, idx) => (
            <DevHubGrid key={idx} item sm={12} lg={6} xl={4}>
              <DeviceCard device={device} showAppName />
            </DevHubGrid>
          ))}
        </DevHubGrid>
      </DevHubBox>
      <DevHubBox display="flex" justifyContent="center" mt={2}>
        {loadingState ? (
          <DevHubCircularProgress />
        ) : (
          <DevHubButton
            disabled={!hasMoreResults || devices.length === 0 || loadingState}
            variant="contained"
            height="large"
            color="primary"
            onClick={handleLoadMore}
          >
            {hasMoreResults && devices.length > 0
              ? 'Load More'
              : 'No More Devices'}
          </DevHubButton>
        )}
      </DevHubBox>
    </Content>
  );
};

export default DeviceListPage;
